import { useEffect, useRef, useState } from "react";
import {
  RotateCcw,
  RefreshCw,
  ArrowLeftRight,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  Package,
  User,
  Phone,
  Eye,
  X,
  AlertCircle,
  CheckCircle2,
  UserCheck,
  PackageCheck,
  Truck,
  CreditCard,
  FileText,
  Loader2,
  Settings,
  Save,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useReturnsNotifications } from "../../context/ReturnsNotificationsContext";
import { ReturnsApi } from "../../services/returns-api";
import BaseApi from "../../services/base-api";
import { PERMISSIONS } from "../../utils/permissions";
import { ReturnRequest, ReturnRequestStatus } from "../../types/ReturnRequest";
import LoadingComponent from "../../components/LoadingComponent";
import Pagination from "../../components/Pagination";
import { ImageWithFallback } from "../../constants/image-placeholder";
import Price from "../../components/Currency/Price";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getLocalizedText = (value: any, fallback = ""): string => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  return value.en || value.ar || fallback;
};

const getCustomerName = (rr: ReturnRequest): string => {
  if (!rr.customer) return "-";
  const { firstName = "", lastName = "" } = rr.customer;
  const full = `${firstName} ${lastName}`.trim();
  return full || rr.customer.email || rr.customer.phone || "-";
};

const getOrderNumber = (rr: ReturnRequest): string => {
  if (!rr.order) return rr.orderId?.slice(-8).toUpperCase() || "-";
  return (rr.order as any).orderNumber || rr.orderId?.slice(-8).toUpperCase() || "-";
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Status + Type config ─────────────────────────────────────────────────────

interface StatusMeta {
  label: string;
  color: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
}

const STATUS_MAP: Record<ReturnRequestStatus, StatusMeta> = {
  requested:        { label: "Requested",        color: "bg-yellow-100 text-yellow-800 border-yellow-200",  Icon: Clock        },
  approved:         { label: "Approved",          color: "bg-blue-100 text-blue-800 border-blue-200",        Icon: CheckCircle  },
  rejected:         { label: "Rejected",          color: "bg-red-100 text-red-800 border-red-200",           Icon: XCircle      },
  driver_assigned:  { label: "Driver Assigned",   color: "bg-indigo-100 text-indigo-800 border-indigo-200",  Icon: UserCheck    },
  pickup_scheduled: { label: "Pickup Scheduled",  color: "bg-purple-100 text-purple-800 border-purple-200",  Icon: Calendar     },
  picked_up:        { label: "Picked Up",         color: "bg-purple-100 text-purple-800 border-purple-200",  Icon: PackageCheck },
  pickup_rejected:  { label: "Pickup Rejected",   color: "bg-red-100 text-red-800 border-red-200",           Icon: AlertCircle  },
  refund_initiated: { label: "Refund Initiated",  color: "bg-orange-100 text-orange-800 border-orange-200",  Icon: RefreshCw    },
  refund_completed: { label: "Refund Completed",  color: "bg-green-100 text-green-800 border-green-200",     Icon: CheckCircle2 },
  refund_failed:    { label: "Refund Failed",     color: "bg-red-100 text-red-800 border-red-200",           Icon: AlertCircle  },
  payment_required: { label: "Payment Required",  color: "bg-orange-100 text-orange-800 border-orange-200",  Icon: CreditCard   },
  completed:        { label: "Completed",         color: "bg-green-100 text-green-800 border-green-200",     Icon: CheckCircle2 },
};

const getStatusMeta = (status: string): StatusMeta =>
  STATUS_MAP[status as ReturnRequestStatus] ?? {
    label: status,
    color: "bg-gray-100 text-gray-800 border-gray-200",
    Icon: Clock,
  };

const TYPE_META = {
  return: {
    label: "Return",
    badge: "bg-indigo-100 text-indigo-800 border-indigo-200",
    Icon: RotateCcw,
  },
  exchange: {
    label: "Exchange",
    badge: "bg-teal-100 text-teal-800 border-teal-200",
    Icon: ArrowLeftRight,
  },
} as const;

const getTypeMeta = (type: string) =>
  TYPE_META[type as keyof typeof TYPE_META] ?? {
    label: type,
    badge: "bg-gray-100 text-gray-800 border-gray-200",
    Icon: Package,
  };

// ─── Available driver shape returned by /api/delivery/drivers/available ───────

interface AvailableDriver {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  activeDeliveries?: number;
  todayDeliveries?: number;
  availability?: "available" | "busy";
}

// ─── Return statuses for filter dropdown ─────────────────────────────────────

const RETURN_STATUSES: ReturnRequestStatus[] = [
  "requested", "approved", "rejected", "driver_assigned",
  "pickup_scheduled", "picked_up", "pickup_rejected",
  "refund_initiated", "refund_completed", "refund_failed",
  "payment_required", "completed",
];

// ─── Component ────────────────────────────────────────────────────────────────

const ReturnsManagementPage = () => {
  const { t } = useTranslation();
  const { success, err } = useToast();
  const { hasPermission } = useAuth();
  const { pendingReturnsCount } = useReturnsNotifications();

  // List state
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<ReturnRequest | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Review modal (approve / reject)
  const [reviewModal, setReviewModal] = useState(false);
  const [reviewDecision, setReviewDecision] = useState<"approved" | "rejected">("approved");
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewing, setReviewing] = useState(false);

  // Assign driver modal
  const [assignDriverModal, setAssignDriverModal] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState<AvailableDriver[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [assigningDriver, setAssigningDriver] = useState(false);

  // Return policy setting (return_policy_days)
  const [policyDays, setPolicyDays] = useState("");
  const [savedPolicyDays, setSavedPolicyDays] = useState("");
  const [savingPolicyDays, setSavingPolicyDays] = useState(false);

  const canManage = hasPermission(PERMISSIONS.MANAGE_RETURNS);
  const canAssignDriver = hasPermission(PERMISSIONS.ASSIGN_DELIVERY_DRIVERS);
  const canEditSettings = hasPermission(PERMISSIONS.EDIT_SETTINGS);

  // Refresh the list when the live SSE count goes up (new request arrived)
  const lastPendingCount = useRef(pendingReturnsCount);
  useEffect(() => {
    if (pendingReturnsCount > lastPendingCount.current) {
      fetchReturns();
    }
    lastPendingCount.current = pendingReturnsCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReturnsCount]);

  useEffect(() => {
    if (!canEditSettings) return;
    const loadPolicyDays = async () => {
      const res = await BaseApi.get("/settings");
      if (res?.success && Array.isArray(res.data)) {
        const setting = res.data.find(
          (item: { key?: string }) => item?.key === "return_policy_days",
        );
        if (setting && setting.value !== undefined && setting.value !== null) {
          const value = String(setting.value);
          setPolicyDays(value);
          setSavedPolicyDays(value);
        }
      }
    };
    loadPolicyDays();
  }, [canEditSettings]);

  const handleSavePolicyDays = async () => {
    const days = Number(policyDays);
    if (!policyDays.trim() || !Number.isInteger(days) || days < 0) {
      err(t("Please enter a valid number of days"));
      return;
    }
    try {
      setSavingPolicyDays(true);
      const res = await BaseApi.put("/settings/return_policy_days", {
        value: days,
        type: "number",
        group: "returns",
        isPublic: true,
        description:
          "Number of days after delivery within which a customer can request a return or exchange",
      });
      if (res?.success) {
        setPolicyDays(String(days));
        setSavedPolicyDays(String(days));
        success(t("Return policy updated"));
      } else {
        err(res?.message || t("Failed to update return policy"));
      }
    } catch (e: any) {
      err(e?.message || t("Failed to update return policy"));
    } finally {
      setSavingPolicyDays(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, startDate, endDate]);

  // Fetch when page or filters change
  useEffect(() => {
    fetchReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, statusFilter, typeFilter, startDate, endDate]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const res = await ReturnsApi.list(currentPage, itemsPerPage, {
        status: statusFilter !== "all" ? statusFilter : undefined,
        type:   typeFilter   !== "all" ? typeFilter   : undefined,
        startDate: startDate || undefined,
        endDate:   endDate   || undefined,
      });
      if (res?.success) {
        setReturns(res.data ?? []);
        setTotalItems(res.pagination?.totalItems ?? (res.data ?? []).length);
      } else {
        err(res?.message || t("Failed to load return requests"));
      }
    } catch {
      err(t("Failed to load return requests"));
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedReturn = async () => {
    if (!selectedReturn) return;
    try {
      const res = await ReturnsApi.getById(selectedReturn.id);
      if (res.success && res.data) setSelectedReturn(res.data);
    } catch {}
  };

  // ── Detail ────────────────────────────────────────────────────────────────

  const handleViewDetail = async (rr: ReturnRequest) => {
    setDetailModal(true);
    setDetailLoading(true);
    setSelectedReturn(rr);
    try {
      const res = await ReturnsApi.getById(rr.id);
      if (res?.success && res.data) {
        setSelectedReturn(res.data);
      } else {
        err(res?.message || t("Failed to load return request details"));
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModal(false);
    setSelectedReturn(null);
  };

  // ── Review ───────────────────────────────────────────────────────────────

  const handleOpenReview = (decision: "approved" | "rejected") => {
    setReviewDecision(decision);
    setReviewNotes("");
    setReviewModal(true);
  };

  const handleReview = async () => {
    if (!selectedReturn) return;
    if (reviewDecision === "rejected" && !reviewNotes.trim()) {
      err(t("Notes are required when rejecting"));
      return;
    }
    try {
      setReviewing(true);
      const res = await ReturnsApi.review(selectedReturn.id, {
        decision: reviewDecision,
        notes: reviewNotes.trim() || undefined,
      });
      if ((res as any)?.success) {
        success(
          t(
            reviewDecision === "approved"
              ? "Request approved successfully"
              : "Request rejected successfully",
          ),
        );
        setReviewModal(false);
        await refreshSelectedReturn();
        fetchReturns();
      } else {
        err(
          (res as any)?.message ||
            t(
              reviewDecision === "approved"
                ? "Failed to approve request"
                : "Failed to reject request",
            ),
        );
      }
    } catch (e: any) {
      err(
        e?.message ||
          t(reviewDecision === "approved" ? "Failed to approve request" : "Failed to reject request"),
      );
    } finally {
      setReviewing(false);
    }
  };

  // ── Assign driver ─────────────────────────────────────────────────────────

  const handleOpenAssignDriver = async () => {
    setAssignDriverModal(true);
    setSelectedDriverId("");
    setDriversLoading(true);
    try {
      const res = await ReturnsApi.getAvailableDrivers();
      if (res?.success) {
        setAvailableDrivers(Array.isArray(res.data) ? res.data : []);
      } else {
        setAvailableDrivers([]);
        err(res?.message || t("Failed to load drivers"));
      }
    } finally {
      setDriversLoading(false);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedReturn || !selectedDriverId) return;
    try {
      setAssigningDriver(true);
      const res = await ReturnsApi.assignDriver(selectedReturn.id, selectedDriverId);
      if ((res as any)?.success) {
        success(t("Driver assigned successfully"));
        setAssignDriverModal(false);
        await refreshSelectedReturn();
        fetchReturns();
      } else {
        err((res as any)?.message || t("Failed to assign driver"));
      }
    } catch (e: any) {
      err(e?.message || t("Failed to assign driver"));
    } finally {
      setAssigningDriver(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (loading && returns.length === 0) {
    return <LoadingComponent title={t("Returns and Exchanges")} />;
  }

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <style>{`
        @keyframes fadeIn   { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown{ from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="mx-auto">
        {/* ── Page header ── */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-gray-600 to-black p-3 rounded-xl shadow-lg">
                <RotateCcw className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
                  {t("Returns and Exchanges")}
                  {pendingReturnsCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-sm font-bold bg-red-500 text-white">
                      {pendingReturnsCount}
                    </span>
                  )}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">{t("track-returns")}</p>
              </div>
            </div>

            <button
              onClick={fetchReturns}
              className="px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm flex items-center gap-2 shadow-lg"
            >
              <RefreshCw size={16} />
              {t("Refresh")}
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm bg-white appearance-none"
            >
              <option value="all">{t("All Status")}</option>
              {RETURN_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(getStatusMeta(s).label)}
                </option>
              ))}
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm bg-white appearance-none"
            >
              <option value="all">{t("All Types")}</option>
              <option value="return">{t("Return")}</option>
              <option value="exchange">{t("Exchange")}</option>
            </select>

            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm"
            />

            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm"
            />
          </div>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              {t("Showing")} {returns.length} {t("of")} {totalItems} {t("requests")}
            </div>

            {canEditSettings && (
              <div className="flex items-center gap-2 text-sm">
                <Settings size={16} className="text-gray-500 flex-shrink-0" />
                <label htmlFor="return-policy-days" className="font-semibold text-gray-700 whitespace-nowrap">
                  {t("Return window (days)")}
                </label>
                <input
                  id="return-policy-days"
                  type="number"
                  min={0}
                  step={1}
                  value={policyDays}
                  onChange={(e) => setPolicyDays(e.target.value)}
                  className="w-20 px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm"
                />
                <button
                  onClick={handleSavePolicyDays}
                  disabled={savingPolicyDays || policyDays === savedPolicyDays}
                  className="px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-black transition-colors font-semibold text-sm flex items-center gap-1 disabled:opacity-50"
                >
                  {savingPolicyDays ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  {t("Save")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── List ── */}
        <div style={{ animation: "fadeIn 0.5s ease-out 0.1s backwards" }}>
          {returns.length === 0 && !loading ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-16 text-center">
              <RotateCcw className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-base sm:text-lg">{t("No return requests found")}</p>
              <p className="text-gray-400 text-sm mt-2">{t("Try adjusting your filters")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {returns.map((rr) => {
                const sm = getStatusMeta(rr.status);
                const tm = getTypeMeta(rr.type);
                const StatusIcon = sm.Icon;
                const TypeIcon = tm.Icon;

                return (
                  <div
                    key={rr.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-4 border-2 border-gray-100 hover:border-indigo-200"
                    style={{ animation: "fadeIn 0.3s ease-out" }}
                  >
                    {/* Card header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 pb-3 border-b-2 border-gray-100">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900 text-lg">
                            {t("Order")} #{getOrderNumber(rr)}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-lg text-xs font-bold border flex items-center gap-1 ${tm.badge}`}
                          >
                            <TypeIcon size={11} />
                            {t(tm.label)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(rr.createdAt)}</div>
                      </div>

                      <span
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 flex items-center gap-1 self-start ${sm.color}`}
                      >
                        <StatusIcon size={14} />
                        {t(sm.label)}
                      </span>
                    </div>

                    {/* Customer row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <User size={15} className="text-gray-400 flex-shrink-0" />
                        <span className="font-medium truncate">{getCustomerName(rr)}</span>
                      </div>
                      {rr.customer?.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Phone size={15} className="text-gray-400 flex-shrink-0" />
                          <a
                            href={`tel:${rr.customer.phone}`}
                            className="hover:text-indigo-600 transition-colors"
                          >
                            {rr.customer.phone}
                          </a>
                        </div>
                      )}
                    </div>

                    {rr.reason && (
                      <div className="mb-3 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 truncate">
                        <span className="font-semibold">{t("Reason")}:</span> {rr.reason}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end pt-3 border-t-2 border-gray-100">
                      <button
                        onClick={() => handleViewDetail(rr)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-semibold flex items-center gap-1"
                      >
                        <Eye size={14} />
                        {t("Details")}
                      </button>
                    </div>
                  </div>
                );
              })}

              {totalItems > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  showPageSizeSelector
                  isLoading={loading}
                  compact
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════ DETAIL MODAL ════════════════════════ */}
      {detailModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeDetailModal}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            {/* Modal header */}
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-xl font-bold text-white">{t("Return Request Details")}</h3>
                {selectedReturn && (
                  <p className="text-sm text-gray-300">
                    {t("Order")} #{getOrderNumber(selectedReturn)}
                  </p>
                )}
              </div>
              <button onClick={closeDetailModal} className="text-white hover:text-gray-200 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {detailLoading && !selectedReturn ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
              </div>
            ) : selectedReturn ? (
              <div className="p-6 space-y-5">

                {/* Status + type */}
                <div className="flex flex-wrap gap-2 items-center">
                  {(() => {
                    const sm = getStatusMeta(selectedReturn.status);
                    const StatusIcon = sm.Icon;
                    return (
                      <span className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 flex items-center gap-1 ${sm.color}`}>
                        <StatusIcon size={15} />
                        {t(sm.label)}
                      </span>
                    );
                  })()}
                  {(() => {
                    const tm = getTypeMeta(selectedReturn.type);
                    const TypeIcon = tm.Icon;
                    return (
                      <span className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 flex items-center gap-1 ${tm.badge}`}>
                        <TypeIcon size={15} />
                        {t(tm.label)}
                      </span>
                    );
                  })()}
                  <span className="text-xs text-gray-500 ml-auto">{formatDate(selectedReturn.createdAt)}</span>
                </div>

                {/* ── Action buttons (context-sensitive) ── */}
                {(() => {
                  const showActions =
                    (selectedReturn.status === "requested" && canManage) ||
                    (selectedReturn.status === "approved" && canAssignDriver);

                  if (!showActions) return null;

                  return (
                    <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                      <p className="text-sm font-bold text-gray-700 mb-3">{t("Actions")}</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedReturn.status === "requested" && canManage && (
                          <>
                            <button
                              onClick={() => handleOpenReview("approved")}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm flex items-center gap-1"
                            >
                              <CheckCircle size={15} />
                              {t("Approve")}
                            </button>
                            <button
                              onClick={() => handleOpenReview("rejected")}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-sm flex items-center gap-1"
                            >
                              <XCircle size={15} />
                              {t("Reject")}
                            </button>
                          </>
                        )}
                        {selectedReturn.status === "approved" && canAssignDriver && (
                          <button
                            onClick={handleOpenAssignDriver}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm flex items-center gap-1"
                          >
                            <UserCheck size={15} />
                            {t("Assign Driver")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Customer ── */}
                {selectedReturn.customer && (
                  <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <User size={17} />
                      {t("Customer Information")}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-blue-700 font-medium">{t("Name")}:</span>{" "}
                        <span className="font-semibold text-gray-900">{getCustomerName(selectedReturn)}</span>
                      </div>
                      {selectedReturn.customer.email && (
                        <div>
                          <span className="text-blue-700 font-medium">{t("Email")}:</span>{" "}
                          <span className="text-gray-900">{selectedReturn.customer.email}</span>
                        </div>
                      )}
                      {selectedReturn.customer.phone && (
                        <div>
                          <span className="text-blue-700 font-medium">{t("Phone")}:</span>{" "}
                          <a href={`tel:${selectedReturn.customer.phone}`} className="text-gray-900 hover:text-blue-700">
                            {selectedReturn.customer.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Order info ── */}
                <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Package size={17} />
                    {t("Order Information")}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex gap-2">
                      <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Order Number")}:</span>
                      <span className="font-semibold">#{getOrderNumber(selectedReturn)}</span>
                    </div>
                    {(selectedReturn.order as any)?.total != null && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Order Total")}:</span>
                        <span className="font-semibold">
                          <Price amount={(selectedReturn.order as any).total} />
                        </span>
                      </div>
                    )}
                    {(selectedReturn.order as any)?.status && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Order Status")}:</span>
                        <span className="capitalize">{t((selectedReturn.order as any).status)}</span>
                      </div>
                    )}
                    {selectedReturn.reason && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Reason")}:</span>
                        <span>{selectedReturn.reason}</span>
                      </div>
                    )}
                    {selectedReturn.comments && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Comments")}:</span>
                        <span>{selectedReturn.comments}</span>
                      </div>
                    )}
                    {selectedReturn.adminReviewNotes && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Review Notes")}:</span>
                        <span className="text-amber-700 font-medium">{selectedReturn.adminReviewNotes}</span>
                      </div>
                    )}
                    {selectedReturn.reviewer && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 font-medium w-32 flex-shrink-0">{t("Reviewer")}:</span>
                        <span>
                          {[selectedReturn.reviewer.firstName, selectedReturn.reviewer.lastName]
                            .filter(Boolean)
                            .join(" ") || selectedReturn.reviewer.email || "-"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Return items ── */}
                {(selectedReturn.items ?? []).length > 0 && (
                  <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Package size={17} />
                      {selectedReturn.type === "exchange" ? t("Item to Exchange") : t("Items to Return")}
                    </h4>
                    <div className="space-y-3">
                      {(selectedReturn.items ?? []).map((item) => {
                        const productImage =
                          item.product?.images?.[0] ||
                          item.orderItem?.product?.images?.[0];
                        const productName =
                          getLocalizedText(item.productName) ||
                          getLocalizedText(item.product?.name) ||
                          getLocalizedText((item.orderItem?.product as any)?.name) ||
                          "-";
                        return (
                          <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                              <ImageWithFallback
                                src={productImage}
                                alt={productName}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 text-sm">{productName}</p>
                              {item.variation?.attributes &&
                                Object.keys(item.variation.attributes).length > 0 && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {Object.entries(item.variation.attributes)
                                      .map(([k, v]) => `${k}: ${v}`)
                                      .join(" · ")}
                                  </p>
                                )}
                              <p className="text-xs text-gray-600 mt-1">
                                {t("Quantity")}: {item.quantity}
                              </p>
                              {item.reason && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {t("Reason")}: {item.reason}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Replacement product (exchange only) ── */}
                {selectedReturn.type === "exchange" &&
                  !selectedReturn.replacementProduct &&
                  selectedReturn.replacementProductId && (
                    <div className="border-2 border-teal-200 rounded-xl p-4 bg-teal-50">
                      <h4 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
                        <ArrowLeftRight size={17} />
                        {t("Replacement Product")}
                      </h4>
                      <div className="space-y-1 text-sm">
                        <p>
                          <span className="text-teal-700 font-medium">{t("Product ID")}:</span>{" "}
                          <span className="font-mono text-gray-900 break-all">{selectedReturn.replacementProductId}</span>
                        </p>
                        {selectedReturn.replacementVariationId && (
                          <p>
                            <span className="text-teal-700 font-medium">{t("Variant ID")}:</span>{" "}
                            <span className="font-mono text-gray-900 break-all">{selectedReturn.replacementVariationId}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                {selectedReturn.type === "exchange" && selectedReturn.replacementProduct && (
                  <div className="border-2 border-teal-200 rounded-xl p-4 bg-teal-50">
                    <h4 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
                      <ArrowLeftRight size={17} />
                      {t("Replacement Product")}
                    </h4>
                    <div className="flex items-start gap-3">
                      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-teal-100">
                        <ImageWithFallback
                          src={selectedReturn.replacementProduct.images?.[0]}
                          alt={getLocalizedText(selectedReturn.replacementProduct.name)}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">
                          {getLocalizedText(selectedReturn.replacementProduct.name)}
                        </p>
                        {selectedReturn.replacementVariation?.attributes &&
                          Object.keys(selectedReturn.replacementVariation.attributes).length > 0 && (
                            <p className="text-sm text-gray-600 mt-0.5">
                              {Object.entries(selectedReturn.replacementVariation.attributes)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(" · ")}
                            </p>
                          )}
                        {selectedReturn.priceDifference != null && (
                          <p className="text-sm font-semibold text-teal-800 mt-1">
                            {t("Price Difference")}: <Price amount={selectedReturn.priceDifference} />
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Media attachments ── */}
                {(selectedReturn.mediaUrls ?? []).length > 0 && (
                  <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText size={17} />
                      {t("Attachments")}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(selectedReturn.mediaUrls ?? []).map((url, i) => (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block h-20 w-20 overflow-hidden rounded-lg bg-gray-100 border-2 border-gray-200 hover:border-indigo-400 transition-colors"
                        >
                          <img src={url} alt="" className="h-full w-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Refund / Payment ── */}
                {(selectedReturn.refundStatus ||
                  selectedReturn.refundAmount != null ||
                  selectedReturn.priceDifference != null ||
                  selectedReturn.differenceOrderId ||
                  selectedReturn.pickupRejectionReason) && (
                  <div className="border-2 border-green-200 rounded-xl p-4 bg-green-50">
                    <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                      <CreditCard size={17} />
                      {t("Refund & Payment")}
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedReturn.refundStatus && (
                        <div className="flex gap-2">
                          <span className="text-green-700 font-medium w-36 flex-shrink-0">{t("Refund Status")}:</span>
                          <span className="font-semibold capitalize">{t(selectedReturn.refundStatus)}</span>
                        </div>
                      )}
                      {selectedReturn.refundAmount != null && (
                        <div className="flex gap-2">
                          <span className="text-green-700 font-medium w-36 flex-shrink-0">{t("Refund Amount")}:</span>
                          <span className="font-semibold"><Price amount={selectedReturn.refundAmount} /></span>
                        </div>
                      )}
                      {selectedReturn.priceDifference != null && (
                        <div className="flex gap-2">
                          <span className="text-green-700 font-medium w-36 flex-shrink-0">{t("Price Difference")}:</span>
                          <span className="font-semibold"><Price amount={selectedReturn.priceDifference} /></span>
                        </div>
                      )}
                      {selectedReturn.differenceOrderId && (
                        <div className="flex gap-2">
                          <span className="text-green-700 font-medium w-36 flex-shrink-0">{t("Difference Order")}:</span>
                          <span className="font-mono break-all">{selectedReturn.differenceOrderId}</span>
                        </div>
                      )}
                      {selectedReturn.pickupRejectionReason && (
                        <div className="flex gap-2">
                          <span className="text-red-700 font-medium w-36 flex-shrink-0">{t("Pickup Rejection")}:</span>
                          <span className="text-red-800">{selectedReturn.pickupRejectionReason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Driver / Delivery ── */}
                {selectedReturn.delivery && (
                  <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <Truck size={17} />
                      {t("Driver Information")}
                    </h4>
                    {selectedReturn.delivery.driver ? (
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-blue-700 font-medium">{t("Name")}:</span>{" "}
                          <span className="font-semibold text-gray-900">
                            {[
                              selectedReturn.delivery.driver.firstName,
                              selectedReturn.delivery.driver.lastName,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </span>
                        </div>
                        {selectedReturn.delivery.driver.phone && (
                          <div>
                            <span className="text-blue-700 font-medium">{t("Phone")}:</span>{" "}
                            <a
                              href={`tel:${selectedReturn.delivery.driver.phone}`}
                              className="text-gray-900 hover:text-blue-700"
                            >
                              {selectedReturn.delivery.driver.phone}
                            </a>
                          </div>
                        )}
                        <div>
                          <span className="text-blue-700 font-medium">{t("Delivery Status")}:</span>{" "}
                          <span className="capitalize font-medium">{selectedReturn.delivery.status}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-blue-700 text-sm">{t("No driver assigned")}</p>
                    )}
                  </div>
                )}

                {/* ── History timeline ── */}
                {(selectedReturn.history ?? []).length > 0 && (
                  <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock size={17} />
                      {t("History")}
                    </h4>
                    <div className="space-y-0">
                      {[...(selectedReturn.history ?? [])].reverse().map((entry, i, arr) => {
                        const sm = getStatusMeta(entry.status);
                        const StatusIcon = sm.Icon;
                        return (
                          <div key={i} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className={`p-1.5 rounded-full border-2 ${sm.color} flex-shrink-0`}>
                                <StatusIcon size={13} />
                              </div>
                              {i < arr.length - 1 && (
                                <div className="w-0.5 bg-gray-200 flex-1 min-h-[24px] my-1" />
                              )}
                            </div>
                            <div className={`flex-1 ${i < arr.length - 1 ? "pb-4" : ""}`}>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded border ${sm.color}`}>
                                  {t(sm.label)}
                                </span>
                                {entry.actorRole && (
                                  <span className="text-xs text-gray-500 capitalize">{entry.actorRole}</span>
                                )}
                                <span className="text-xs text-gray-400 ml-auto">{formatDate(entry.timestamp)}</span>
                              </div>
                              {(entry.notes || entry.reason) && (
                                <p className="mt-1 text-sm text-gray-700">{entry.notes || entry.reason}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* ════════════════════════ REVIEW MODAL ════════════════════════ */}
      {reviewModal && selectedReturn && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
          onClick={() => setReviewModal(false)}
          style={{ animation: "fadeIn 0.2s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div
              className={`px-6 py-4 rounded-t-2xl flex items-center justify-between ${
                reviewDecision === "approved"
                  ? "bg-gradient-to-r from-green-700 to-green-900"
                  : "bg-gradient-to-r from-red-700 to-red-900"
              }`}
            >
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                {reviewDecision === "approved" ? (
                  <CheckCircle size={20} />
                ) : (
                  <XCircle size={20} />
                )}
                {reviewDecision === "approved" ? t("Approve Request") : t("Reject Request")}
              </h3>
              <button onClick={() => setReviewModal(false)} className="text-white hover:text-gray-200">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                {t("Order")} #{getOrderNumber(selectedReturn)} · {t(getTypeMeta(selectedReturn.type).label)}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Review Notes")}
                  {reviewDecision === "rejected" && <span className="text-red-500"> *</span>}
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  placeholder={
                    reviewDecision === "approved"
                      ? t("Optional notes for the customer")
                      : t("Required — explain why this request is rejected")
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none resize-none text-sm"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setReviewModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleReview}
                  disabled={
                    reviewing || (reviewDecision === "rejected" && !reviewNotes.trim())
                  }
                  className={`flex-1 px-6 py-3 text-white rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${
                    reviewDecision === "approved"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {reviewing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {reviewing
                    ? t("Submitting...")
                    : reviewDecision === "approved"
                    ? t("Approve")
                    : t("Reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════ ASSIGN DRIVER MODAL ════════════════════════ */}
      {assignDriverModal && selectedReturn && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
          onClick={() => {
            setAssignDriverModal(false);
            setSelectedDriverId("");
          }}
          style={{ animation: "fadeIn 0.2s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-blue-700 to-blue-900 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <UserCheck size={22} />
                {t("Assign Driver")}
              </h3>
              <button
                onClick={() => {
                  setAssignDriverModal(false);
                  setSelectedDriverId("");
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-900">
                {t("Order")} #{getOrderNumber(selectedReturn)} · {t(getTypeMeta(selectedReturn.type).label)}
              </div>

              <p className="text-sm font-bold text-gray-700 mb-3">{t("Select Available Driver")}</p>

              {driversLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                </div>
              ) : availableDrivers.length === 0 ? (
                <div className="text-center py-8">
                  <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">{t("No available drivers")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableDrivers.map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => setSelectedDriverId(driver.id)}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        selectedDriverId === driver.id
                          ? "border-blue-400 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                            {(driver.firstName?.[0] || driver.name?.[0] || "?").toUpperCase()}
                            {driver.lastName?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {[driver.firstName, driver.lastName].filter(Boolean).join(" ") ||
                                driver.name ||
                                "-"}
                            </p>
                            {driver.phone && (
                              <p className="text-sm text-gray-600">{driver.phone}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {driver.availability && (
                                <span
                                  className={`text-xs px-2 py-0.5 rounded font-semibold ${
                                    driver.availability === "available"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {t(driver.availability === "available" ? "Available" : "Busy")}
                                </span>
                              )}
                              {driver.activeDeliveries != null && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-semibold">
                                  {driver.activeDeliveries} {t("active")}
                                </span>
                              )}
                              {driver.todayDeliveries != null && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-semibold">
                                  {driver.todayDeliveries} {t("today")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {selectedDriverId === driver.id && (
                          <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 pb-6 pt-2 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => {
                  setAssignDriverModal(false);
                  setSelectedDriverId("");
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleAssignDriver}
                disabled={assigningDriver || !selectedDriverId}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {assigningDriver && <Loader2 className="w-4 h-4 animate-spin" />}
                {assigningDriver ? t("Assigning...") : t("Assign Driver")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnsManagementPage;
